import { useState, useEffect, useCallback } from "react"
import { db } from "lib/firebase"
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore"

/**
 * Per-person Firestore hook for travel data.
 * Listens to travel/{personId} and provides toggle functionality.
 */
export const useTravelData = (personId) => {
  const [visitedUsa, setVisitedUsa] = useState([])
  const [visitedWorld, setVisitedWorld] = useState([])
  const [loading, setLoading] = useState(true)

  const docId = personId?.toLowerCase()

  useEffect(() => {
    if (!docId) return

    const unsubscribe = onSnapshot(
      doc(db, "travel", docId),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setVisitedUsa(data.usa || [])
          setVisitedWorld(data.world || [])
        } else {
          setVisitedUsa([])
          setVisitedWorld([])
        }
        setLoading(false)
      },
      (error) => {
        console.error("Error fetching travel data:", error)
        setLoading(false)
      },
    )

    return () => unsubscribe()
  }, [docId])

  const togglePlace = useCallback(
    (scope, placeId) => {
      if (!docId || !placeId) return

      const setter = scope === "usa" ? setVisitedUsa : setVisitedWorld
      const field = scope === "usa" ? "usa" : "world"

      setter((prev) => {
        const isVisited = prev.includes(placeId)
        const updated = isVisited
          ? prev.filter((id) => id !== placeId)
          : [...prev, placeId]

        // Write to Firestore
        setDoc(
          doc(db, "travel", docId),
          { [field]: updated, lastUpdated: serverTimestamp() },
          { merge: true },
        ).catch((err) => console.error("Error saving travel data:", err))

        return updated
      })
    },
    [docId],
  )

  return { visitedUsa, visitedWorld, loading, togglePlace }
}
